import { computed } from 'vue';
import { usePage } from '@inertiajs/vue3';

/**
 * Composable for timezone-aware date formatting.
 *
 * Two timezone regimes coexist in this codebase:
 * - `source: 'user'` (default): honors the canonical `auth.timezone`
 *   shared prop (server-side: `User->timezone ?? config('app.user_timezone')`),
 *   then falls back to `auth.user.timezone` for older payloads, then to
 *   the browser TZ. Used by PDF-style flows (REM-254) where the
 *   server-stored preference defines the report TZ.
 * - `source: 'browser'`: ignores the stored user TZ and always uses the
 *   browser's resolved TZ. Used by the CRM UI (REM-261) so each agent
 *   sees datetimes in their own local zone regardless of profile state.
 *
 * @param {{ source?: 'user'|'browser' }} [options]
 * @returns {Object} Date formatting functions
 */
export function useDateFormat({ source = 'user' } = {}) {
    const page = usePage();

    const timezone = computed(() => {
        if (source === 'browser') {
            return Intl.DateTimeFormat().resolvedOptions().timeZone
                || 'America/El_Salvador';
        }

        return page.props.auth?.timezone
            || page.props.auth?.user?.timezone
            || Intl.DateTimeFormat().resolvedOptions().timeZone
            || 'America/El_Salvador';
    });

    /**
     * Get the user's locale preference
     */
    const locale = computed(() => {
        return page.props.auth?.user?.language === 'en' ? 'en-US' : 'es-ES';
    });

    /**
     * Format a date string to a localized date (e.g., "15 ene 2024")
     * @param {string|Date} dateString - The date to format
     * @param {Object} options - Optional Intl.DateTimeFormat options
     * @returns {string} Formatted date
     */
    function formatDate(dateString, options = {}) {
        if (!dateString) return '';

        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';

        const defaultOptions = {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            timeZone: timezone.value,
        };

        return date.toLocaleDateString(locale.value, { ...defaultOptions, ...options });
    }

    /**
     * Format a date string to a localized date and time (e.g., "15 ene 2024, 14:30")
     * @param {string|Date} dateString - The date to format
     * @param {Object} options - Optional Intl.DateTimeFormat options
     * @returns {string} Formatted date and time
     */
    function formatDateTime(dateString, options = {}) {
        if (!dateString) return '';

        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';

        const defaultOptions = {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            timeZone: timezone.value,
        };

        return date.toLocaleString(locale.value, { ...defaultOptions, ...options });
    }

    /**
     * Format a date string to time only (e.g., "14:30")
     * @param {string|Date} dateString - The date to format
     * @param {Object} options - Optional Intl.DateTimeFormat options
     * @returns {string} Formatted time
     */
    function formatTime(dateString, options = {}) {
        if (!dateString) return '';

        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';

        const defaultOptions = {
            hour: '2-digit',
            minute: '2-digit',
            timeZone: timezone.value,
        };

        return date.toLocaleTimeString(locale.value, { ...defaultOptions, ...options });
    }

    /**
     * Format a date string to a long date format (e.g., "15 de enero de 2024")
     * @param {string|Date} dateString - The date to format
     * @returns {string} Formatted long date
     */
    function formatLongDate(dateString) {
        if (!dateString) return '';

        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';

        return date.toLocaleDateString(locale.value, {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
            timeZone: timezone.value,
        });
    }

    /**
     * Format a date string to month and year only (e.g., "enero 2024")
     * @param {string|Date} dateString - The date to format
     * @returns {string} Formatted month and year
     */
    function formatMonthYear(dateString) {
        if (!dateString) return '';

        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';

        return date.toLocaleDateString(locale.value, {
            month: 'long',
            year: 'numeric',
            timeZone: timezone.value,
        });
    }

    /**
     * Format a date string to a full date and time (e.g., "15 de enero de 2024, 14:30")
     * @param {string|Date} dateString - The date to format
     * @returns {string} Formatted full date and time
     */
    function formatFullDateTime(dateString) {
        if (!dateString) return '';

        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';

        return date.toLocaleString(locale.value, {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            timeZone: timezone.value,
        });
    }

    /**
     * Format a date string to a relative time (e.g., "hace 5 min", "hace 2 días")
     * @param {string|Date} dateString - The date to format
     * @returns {string} Relative time string
     */
    function formatRelative(dateString) {
        if (!dateString) return '';

        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';

        const now = new Date();
        const diffMs = now - date;
        const diffSecs = Math.floor(diffMs / 1000);
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        const isSpanish = locale.value.startsWith('es');

        if (diffSecs < 60) {
            return isSpanish ? 'ahora' : 'now';
        }
        if (diffMins < 60) {
            return isSpanish ? `hace ${diffMins} min` : `${diffMins} min ago`;
        }
        if (diffHours < 24) {
            return isSpanish ? `hace ${diffHours}h` : `${diffHours}h ago`;
        }
        if (diffDays < 7) {
            return isSpanish ? `hace ${diffDays}d` : `${diffDays}d ago`;
        }

        // Fall back to short date for older dates
        return formatDate(dateString, { year: undefined });
    }

    /**
     * Format a date for use in form inputs (YYYY-MM-DD)
     * @param {string|Date} dateString - The date to format
     * @returns {string} ISO date string (YYYY-MM-DD)
     */
    function formatInputDate(dateString) {
        if (!dateString) return '';

        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';

        // Format in the user's timezone
        const options = {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            timeZone: timezone.value,
        };

        const parts = new Intl.DateTimeFormat('en-CA', options).formatToParts(date);
        const year = parts.find(p => p.type === 'year')?.value;
        const month = parts.find(p => p.type === 'month')?.value;
        const day = parts.find(p => p.type === 'day')?.value;

        return `${year}-${month}-${day}`;
    }

    return {
        timezone,
        locale,
        formatDate,
        formatDateTime,
        formatTime,
        formatLongDate,
        formatMonthYear,
        formatFullDateTime,
        formatRelative,
        formatInputDate,
    };
}

/**
 * Extract YYYY-MM-DD from a Date/datetime in the browser's local TZ.
 *
 * Use this for "civil dates" sent to the backend (contract dates, billing
 * dates, filter ranges). The naive `.toISOString().split('T')[0]` form
 * breaks when the local Date crosses midnight UTC, shifting the day.
 *
 * @param {Date|string|number|null} value
 * @returns {string|null}
 */
export function toLocalDateString(value) {
    if (!value) return null;
    const d = value instanceof Date ? value : new Date(value);
    if (isNaN(d.getTime())) return null;
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Today's date as YYYY-MM-DD in the browser's local TZ.
 *
 * @returns {string}
 */
export function getTodayLocal() {
    return toLocalDateString(new Date());
}

/**
 * Extract YYYY-MM-DD from any datetime, evaluated in the browser's local TZ.
 *
 * Splitting an ISO string by "T" leaks UTC wall-clock into form fields and
 * shifts them by the user's offset. Use this instead when populating `<input
 * type="date">` from a backend ISO string.
 *
 * @param {Date|string|number|null} value
 * @returns {string|null}
 */
export function extractLocalDate(value) {
    return toLocalDateString(value);
}

/**
 * Extract HH:mm from any datetime, evaluated in the browser's local TZ.
 *
 * @param {Date|string|number|null} value
 * @returns {string|null}
 */
export function extractLocalTime(value) {
    if (!value) return null;
    const d = value instanceof Date ? value : new Date(value);
    if (isNaN(d.getTime())) return null;
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/**
 * Combine a YYYY-MM-DD + HH:mm pair (interpreted in the browser's local TZ)
 * into a UTC ISO 8601 string suitable for sending to the backend
 * (APP_TIMEZONE=UTC).
 *
 * @param {string|null} dateStr
 * @param {string|null} timeStr
 * @returns {string|null}
 */
export function combineLocalToISO(dateStr, timeStr) {
    if (!dateStr || !timeStr) return null;
    const [y, mo, d] = dateStr.split('-').map(Number);
    const [h, mi] = timeStr.split(':').map(Number);
    const dt = new Date(y, mo - 1, d, h, mi);
    if (isNaN(dt.getTime())) return null;
    return dt.toISOString();
}

/**
 * Convert a Date object (e.g. from a PrimeVue DatePicker with showTime, which
 * binds a JS Date) into a UTC ISO 8601 string. Strings and timestamps pass
 * through `new Date(...)` first.
 *
 * @param {Date|string|number|null} value
 * @returns {string|null}
 */
export function dateToISO(value) {
    if (!value) return null;
    const d = value instanceof Date ? value : new Date(value);
    if (isNaN(d.getTime())) return null;
    return d.toISOString();
}

/**
 * Format a civil date string ("YYYY-MM-DD" or any ISO string whose date prefix
 * is the calendar date we want to show). TZ-unaware — does NOT shift the day
 * by the viewer's timezone, which is what we want for date-only columns
 * (birth_date, expected_date, closing_date, etc.).
 *
 * @param {string|null} ymd
 * @param {{ locale?: string, format?: 'short'|'long' }} [options]
 * @returns {string}
 */
export function formatCivilDate(ymd, options = {}) {
    if (!ymd) return '';
    // Back-compat: allow a bare locale string as the second arg.
    const opts = typeof options === 'string' ? { locale: options } : options;
    const locale = opts.locale ?? 'es-ES';
    const format = opts.format ?? 'short';
    const datePart = String(ymd).slice(0, 10);
    const [y, m, d] = datePart.split('-').map(Number);
    if (!y || !m || !d) return '';
    const dt = new Date(y, m - 1, d);
    if (isNaN(dt.getTime())) return '';
    return dt.toLocaleDateString(locale, {
        day: 'numeric',
        month: format === 'long' ? 'long' : 'short',
        year: 'numeric',
    });
}

/**
 * Check whether a value looks like a civil date wire format — either a bare
 * "YYYY-MM-DD" or a Laravel-serialized 'date' cast (ends with `T00:00:00...Z`).
 * Used by <AppDate> to route those values through `formatCivilDate` instead
 * of the TZ-aware formatters, which would shift the day for users in
 * negative-offset zones.
 *
 * @param {unknown} value
 * @returns {boolean}
 */
export function isCivilDateLike(value) {
    if (typeof value !== 'string') return false;
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return true;
    // Laravel 'date' cast → "2026-05-15T00:00:00.000000Z" (always midnight UTC).
    return /^\d{4}-\d{2}-\d{2}T00:00:00(\.\d+)?Z$/.test(value);
}
